/**
 * Donor Profiles API client (weavein) — donor management Phase 4.
 *
 * Wraps /api/donor-profiles/* (donor_profiles, mig 637). Donor-ness is a ROLE
 * layered on an existing member/contact record, not a third members.record_type,
 * and it is the UNION of an explicit profile and anyone with a succeeded gift —
 * so someone who has obviously given never has to be tagged by hand.
 *
 * `searchPeople` is the people-picker used by cohort building and pipeline
 * binding. It searches contacts AND members: the old cohort picker hard-filtered
 * to record_type='contact', which is exactly why members who donate could not be
 * added to a donor cohort.
 */

import { authFetchJson } from '@/lib/authFetch';

export type DonorType = 'individual' | 'household' | 'corporate' | 'foundation' | 'board' | 'anonymous';
export type DonorStatus = 'prospect' | 'active' | 'lapsed' | 'major' | 'recurring';

export const DONOR_TYPES: DonorType[] = [
  'individual',
  'household',
  'corporate',
  'foundation',
  'board',
  'anonymous',
];
export const DONOR_STATUSES: DonorStatus[] = ['prospect', 'active', 'lapsed', 'major', 'recurring'];

export interface DonorProfile {
  member_id: number;
  name: string;
  email?: string | null;
  phone?: string | null;
  record_type: string;
  is_donor: boolean;
  donor_type: DonorType;
  donor_status: DonorStatus;
  solicitation_opt_out: boolean;
  /** true = staff tagged them; false = derived purely from giving history. */
  explicit: boolean;
  lifetime_giving: number;
  gift_count: number;
  last_gift_at?: string | null;
  notes?: string | null;
}

export interface PersonResult {
  id: number;
  name: string;
  email?: string | null;
  phone?: string | null;
  record_type: string;
  is_donor: boolean;
  lifetime_giving: number;
}

function buildQuery(params: Record<string, any> = {}): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

export async function listDonors(
  params: { search?: string; donor_status?: DonorStatus; donor_type?: DonorType; limit?: number } = {},
): Promise<DonorProfile[]> {
  return authFetchJson<DonorProfile[]>(`/api/donor-profiles/donors${buildQuery(params)}`);
}

export async function getDonorSummary(): Promise<{
  donors: number;
  givers: number;
  prospects: number;
  lifetime_giving: number;
}> {
  return authFetchJson('/api/donor-profiles/donors/summary');
}

/** 200 with is_donor=false for a non-donor, so a record can offer "Mark as donor". */
export async function getDonor(memberId: number): Promise<DonorProfile> {
  return authFetchJson<DonorProfile>(`/api/donor-profiles/donors/${memberId}`);
}

export async function markAsDonor(payload: {
  member_id: number;
  donor_type?: DonorType;
  donor_status?: DonorStatus;
  solicitation_opt_out?: boolean;
  notes?: string;
}): Promise<DonorProfile> {
  return authFetchJson<DonorProfile>('/api/donor-profiles/donors', { method: 'POST', jsonBody: payload });
}

export async function updateDonor(
  memberId: number,
  payload: {
    donor_type?: DonorType;
    donor_status?: DonorStatus;
    solicitation_opt_out?: boolean;
    notes?: string;
  },
): Promise<DonorProfile> {
  return authFetchJson<DonorProfile>(`/api/donor-profiles/donors/${memberId}`, {
    method: 'PATCH',
    jsonBody: payload,
  });
}

/** Removes the explicit tag. Someone who has actually given stays a donor by derivation. */
export async function unmarkDonor(memberId: number): Promise<void> {
  await authFetchJson<void>(`/api/donor-profiles/donors/${memberId}`, { method: 'DELETE' });
}

/**
 * People picker across contacts AND members.
 * `record_type` narrows explicitly; omitting it searches both (the correct default).
 */
export async function searchPeople(payload: {
  search?: string;
  record_type?: 'contact' | 'member';
  limit?: number;
  exclude_cohort_id?: number;
}): Promise<PersonResult[]> {
  return authFetchJson<PersonResult[]>('/api/donor-profiles/donors/search', {
    method: 'POST',
    jsonBody: payload,
  });
}
